import { TOKENS } from '@felix/ui';
import { CopyButton, Group, Loader, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './TotpEnrollment.module.css';

/**
 * Doğrulama uygulamasını bağlamanın ortak parçaları: QR kodu ve elle girilecek anahtar. Eski giriş
 * aşaması (`/two-factor/setup`) ve Ayarlar ▸ Hesap'taki "Turn on" penceresi aynı görünümü kullanır
 * (sahip 2026-11-01).
 */

/** Anahtar dörtlü gruplar halinde; elle yazmak kolay olsun. */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/** `otpauth://` adresinin QR görüntüsü (data URL); adres yokken `null`. */
export function useTotpQr(otpauthUri: string | null): string | null {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!otpauthUri) {
      setQr(null);
      return;
    }
    let live = true;
    QRCode.toDataURL(otpauthUri, {
      margin: 1,
      width: 216,
      color: { dark: TOKENS['navy-900'], light: TOKENS.surface },
    })
      .then((url) => {
        if (live) setQr(url);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [otpauthUri]);
  return qr;
}

/** QR çerçevesi: görüntü hazır değilken yükleniyor göstergesi. */
export function TotpQr({ qr }: { qr: string | null }) {
  const { t } = useTranslation();
  return (
    <div className={classes.qrFrame}>
      {qr ? (
        <img src={qr} width={216} height={216} alt={t('totpSetup.qrAlt')} />
      ) : (
        <Loader aria-label={t('common.loading')} />
      )}
    </div>
  );
}

/** "Can’t scan?" anahtarı: dörtlü gruplar ve kopyala düğmesi. */
export function TotpSecret({ secret }: { secret: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <Text size="xs" c="dimmed" mb={6}>
        {t('totpSetup.manual')}
      </Text>
      <Group gap={8} wrap="nowrap" className={classes.secret}>
        <code className={classes.secretText} data-testid="totp-secret">
          {groupSecret(secret)}
        </code>
        <CopyButton value={secret}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('totpSetup.copied') : t('totpSetup.copy')} withArrow>
              <UnstyledButton
                onClick={copy}
                className={classes.copy}
                aria-label={t('totpSetup.copy')}
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </UnstyledButton>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </div>
  );
}
